/**
 * Constructs the public URL for an object in a public S3 bucket.
 * Assumes the standard virtual-hosted style URL format.
 * Adjust the URL format if your bucket uses path-style access or a specific region endpoint.
 *
 * @param {string} bucketName - The name of the public S3 bucket.
 * @param {string} key - The path (key) of the object within the bucket.
 * @returns {string} The full public URL to the S3 object.
 */
export function getPublicS3Url(bucketName, key) {
  if (!bucketName || !key) {
    console.warn("Bucket name or key is missing for getPublicS3Url");
    // Return a default placeholder or empty string if appropriate
    return "https://go-ex-v2-public.s3.amazonaws.com/assets/images/nh.png";
  }
  // Ensure the key doesn't have a leading slash which can cause issues
  const cleanKey = key.startsWith("/") ? key.substring(1) : key;
  // Standard public URL format (adjust if needed, e.g., add region)
  return `https://${bucketName}.s3.amazonaws.com/${cleanKey}`;
}

/** Where the API lives, the same way `services/api.js` resolves it. */
const apiOrigin = () =>
  (process.env.REACT_APP_API_URL || 'http://localhost:8000/').replace(/\/+$/, '');

/**
 * A file URL the API handed back, made absolute.
 *
 * In development the storage is the filesystem, and Django answers with a path
 * relative to itself (`/labels/…`, `/pod_images/…`). Left alone, the browser
 * resolves it against the front and gets the front's own page back. Anything
 * else comes back untouched.
 *
 * @param {string} url
 * @returns {string}
 */
export function toApiFileUrl(url) {
  if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')) {
    return `${apiOrigin()}${url}`;
  }
  return url;
}

/**
 * Turns the label URL the backend stored into the one the browser may fetch.
 *
 * Labels are stored through the CDN domain, and that distribution caches the
 * CORS header without keying on the origin: whichever origin warms an object
 * keeps it for the whole TTL, and every other one gets a header that does not
 * match and a blocked fetch. The bucket answers CORS per request, so the key is
 * re-pointed at it.
 *
 * In development the storage is the filesystem, and Django hands back a
 * relative path (`/labels/items/label_3543_1.pdf`). Left alone, the browser
 * resolves that against whoever asked — the dev server on :3002 — and every
 * label 404s without a request ever leaving the machine. It belongs to the API,
 * so that is what it is resolved against.
 *
 * A value that is neither comes back untouched rather than swallowed.
 *
 * @param {string} storedUrl - `label` as the API returns it
 * @returns {string} URL to fetch or open
 */
export function toPublicLabelUrl(storedUrl) {
  if (!storedUrl) return storedUrl;

  if (storedUrl.startsWith('/')) return toApiFileUrl(storedUrl);

  try {
    const key = new URL(storedUrl).pathname.substring(1);
    return getPublicS3Url(process.env.REACT_APP_S3_BUCKET_NAME, key);
  } catch {
    return storedUrl;
  }
}

/**
 * Retrieves public S3 URLs for a given set of keys, using localStorage for caching.
 *
 * @param {Object.<string, string>} imageskeyRoutes - An object mapping semantic keys to S3 object keys.
 * @param {string} itemName - A unique identifier for the cache key (e.g., 'MobileLayout').
 * @param {number} [cacheDurationMinutes=60] - How long the cache is valid in minutes.
 * @returns {Promise<Object.<string, string>>} A promise that resolves to an object mapping semantic keys to their public S3 URLs.
 */
export async function getImagesUrls(
  imageskeyRoutes,
  itemName,
  cacheDurationMinutes = 60
) {
  const cacheKeyBase = `${itemName}PublicImageUrls`;
  const timestampKey = `${cacheKeyBase}Timestamp`;
  const cacheDurationMillis = cacheDurationMinutes * 60 * 1000;

  const now = new Date().getTime();
  const cachedTimestamp = localStorage.getItem(timestampKey);
  const cachedUrlsJson = localStorage.getItem(cacheKeyBase);

  let imageUrls = {};

  if (
    cachedTimestamp &&
    cachedUrlsJson &&
    now - parseInt(cachedTimestamp, 10) < cacheDurationMillis
  ) {
    try {
      imageUrls = JSON.parse(cachedUrlsJson);
      let needsUpdate = false;

      for (const key in imageskeyRoutes) {
        if (!imageUrls[key]) {
          console.log(`Cache miss for key: ${key}. Generating public URL.`);
          const objectKey = imageskeyRoutes[key];
          imageUrls[key] = objectKey
            ? getPublicS3Url(process.env.REACT_APP_S3_BUCKET_NAME, objectKey)
            : "https://go-ex-v2-public.s3.amazonaws.com/assets/images/nh.png"; // Default for empty keys
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        localStorage.setItem(cacheKeyBase, JSON.stringify(imageUrls));
      }

      console.log("Returning image URLs from cache (potentially updated).");
      return imageUrls;
    } catch (e) {
      console.error("Error parsing cached image URLs, fetching fresh.", e);
    }
  }

  console.log("Cache invalid or missing, generating fresh public URLs.");
  imageUrls = {};

  for (const key in imageskeyRoutes) {
    const objectKey = imageskeyRoutes[key];
    if (objectKey && objectKey.length > 0) {
      imageUrls[key] = getPublicS3Url(
        process.env.REACT_APP_S3_BUCKET_NAME,
        objectKey
      );
    } else {
      imageUrls[key] =
        "https://go-ex-v2-public.s3.amazonaws.com/assets/images/nh.png";
    }
  }

  try {
    localStorage.setItem(cacheKeyBase, JSON.stringify(imageUrls));
    localStorage.setItem(timestampKey, now.toString());
  } catch (e) {
    console.error("Failed to save image URLs to localStorage.", e);
  }

  return imageUrls;
}
